import { clearLegacyMicrosoftInteraction } from './msalMigration';

const loginRequest = {
  scopes: ['openid', 'profile', 'User.Read']
};

const TENANT_ID = import.meta.env.VITE_AZURE_TENANT_ID || '';
const CLIENT_ID = import.meta.env.VITE_AZURE_CLIENT_ID || '';

class MSALService {
  msalInstance = null;
  account = null;
  _initPromise = null;
  _lastAccessToken = null;

  async initialize() {
    if (this._initPromise) return this._initPromise;
    this._initPromise = this._doInit();
    return this._initPromise;
  }

  async _doInit() {
    try {
      clearLegacyMicrosoftInteraction(CLIENT_ID);
      const { PublicClientApplication } = await import('@azure/msal-browser');

      const msalConfig = {
        auth: {
          clientId: CLIENT_ID,
          authority: `https://login.microsoftonline.com/${TENANT_ID}`,
          redirectUri: window.location.origin + '/login',
          navigateToLoginRequestUrl: false,
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: true,
        }
      };

      this.msalInstance = new PublicClientApplication(msalConfig);
      await this.msalInstance.initialize();

      const response = await this.msalInstance.handleRedirectPromise();
      if (response) {
        this.account = response.account;
        this._lastAccessToken = response.accessToken || null;
      } else {
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          this.account = accounts[0];
        }
      }
    } catch (error) {
      console.error('Failed to initialize MSAL:', error);
    }
  }

  async login() {
    if (!this.msalInstance) throw new Error('MSAL not initialized');
    return this.msalInstance.loginRedirect(loginRequest);
  }

  consumeLoginToken() {
    const token = this._lastAccessToken;
    this._lastAccessToken = null;
    return token;
  }

  async getAccessToken() {
    if (!this.msalInstance || !this.account) throw new Error('User not authenticated');

    const request = { scopes: ['User.Read'], account: this.account };

    try {
      const response = await this.msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch {
      await this.msalInstance.acquireTokenRedirect(request);
      return null;
    }
  }

  getAccount() {
    return this.account;
  }

  async logout() {
    if (this.msalInstance) {
      try {
        const accounts = this.msalInstance.getAllAccounts();
        for (const acct of accounts) {
          this.msalInstance.getTokenCache().removeAccount(acct);
        }
      } catch {
        // ignore
      }
    }

    Object.keys(localStorage).filter(k =>
      k.startsWith('msal.') || k.includes('login.windows.net') || k.includes('login.microsoftonline.com')
    ).forEach(k => localStorage.removeItem(k));

    sessionStorage.removeItem('msal_auto_exchange_attempted');
    this.account = null;
    this._initPromise = null;
  }
}

export const msalService = new MSALService();
